<template>
  <TableTemplate
    v-model="tableResult"
    :columns="tableColumns"
    :columnVariables="tableColumnVariables"
    :skeletonClass="skeletonClass"
    :selected="selected"
    :enable-sorting="true"
    :remove-checkboxes="true"
    :page="page"
    :max="max"

    date-format="dd-MM-yyyy"

    @update-column-order="updateColumnOrder"
    @set-page="(v) => (page = v)"
    ref="table"
  >
    <template #above>
      <TableHeader
        title="Nieuwe replenishment"
        :show-button="showButton"
        :alwaysShowMultiSelectCheckbox="true"
        :refetch="refetch"
        :exportParams="exportEndpointParams"
        :deliveryExport="true"
        csv-name="delivery_stock"
        export-endpoint="/api/delivery-exports"
        :order-number="orderNumber"
        :comment="comment"
        @customize-layout="customizeLayout"
        @setting-layout="customizeSetting"
      >
        <template #header-buttons-before="props">
          <slot name="header-buttons-before" v-bind="props"></slot>
        </template>
      </TableHeader>

      <div class="d-flex mb-md-2 flex-column flex-md-row flex-wrap replenishment-filters">
        <TableMainSearchField
          class="mr-md-2 search-filed search-filed-main"
          placeholder="Bestellingsnummer"
          :removeSearch="true"
          v-model="number"
        />
        <TableMainSearchField
          class="mr-md-2 search-filed search-filed-main"
          placeholder="Opmerking"
          :removeSearch="true"
          v-model="new_comment"
        />

        <FormDatePicker v-model="newDatePlannedShipment"
          class="mr-md-2"
          placeholder="Datum geplande verzending"
          :update-model-on-confirmation="true"
          :table="true"
        />

        <FormDatePicker v-model="newOrderDateShipment"
          class="mr-md-2"
          placeholder="Besteldatum verzending"
          :update-model-on-confirmation="true"
          :table="true"
        />
      </div>
      <div class="d-flex flex-column flex-md-row flex-wrap replenishment-filters">
        <FormSearchSelector
          class="mr-md-2"
          endpoint="/api/products"
          id="product"
          placeholder="Select product"
          ref="product"
          :search-parameter="isMobile ? 'ean13' : 'search'"
          :table="true"
          titleField="name"
          uid="sku"
          v-model="newDelivery.sku"
          v-slot="item"
          :params="{ page_size: 50 }"
          @option-select="selectItem"
          :infiniteScrolling=true
          :disable-initial-loading="isMobile"
        >
          <ProductListItem v-bind="item" />
        </FormSearchSelector>

        <div
          class="search-filed download-dropdown position-relative mr-md-2"
          ref="download-dropdown"
        >
          <div class="d-flex main-box" @click="isActive = !isActive">
            <input :placeholder="label" disabled class="flex-grow-1 p-2" />

            <div
              class="
                icon-wrapper
                flex-shrink-0
                p-2
                d-flex
                justify-content-center
                align-items-center
              "
            >
              <img
                src="/img/up-arrow.svg"
                class="up-arrow-part"
                :class="{ isActive }"
                width="20"
                height="20"
                alt="Up-arrow"
              />
            </div>
          </div>
          <div v-if="isActive" class="export-div">
            <ReplenishmentDropdownTable />
          </div>
        </div>
        <button class="btn" @click="confirmBulkDelete()">
            Alles verwijderen
        </button>
      </div>
    </template>

    <template #cell-quantity="{ item }">
      <EditableTableItem
        :value="item.quantity"
        :always-open="true"
        @save="(quantity) => updateDeliveryWithMessage(item.id, { quantity })"
      >
        {{ item.quantity }}
      </EditableTableItem>
    </template>

    <template v-if="loaded" #row-action="{ item }">
      <div class="delete-icon" @click="confirmDelete(item)">
        <svg width="13" height="15" viewBox="0 0 9 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.45183 4.08449L6.83739 9H2.53631L1.92188 4.08449" stroke="white" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M1 2.85561H8.37327" stroke="white" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M3.12598 2.67742V1.61444C3.12598 1.45148 3.19071 1.29519 3.30594 1.17997C3.42117 1.06474 3.57746 1 3.74042 1H5.58373C5.74669 1 5.90298 1.06474 6.01821 1.17997C6.13344 1.29519 6.19817 1.45148 6.19817 1.61444V2.84332" stroke="white" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </template>
    <template #empty-state>
      <EmptyStatePlaceholder title="Er zijn geen adviesbestellingen">
        <svg
          width="18"
          height="18"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 48 48"
        >
          <g transform="matrix(3.4285714285714284,0,0,3.4285714285714284,0,0)">
            <g>
              <path
                d="M4.5,12.5l-4,1,1-3v-9a1,1,0,0,1,1-1h10a1,1,0,0,1,1,1v10a1,1,0,0,1-1,1Z"
                style="
                  fill: none;
                  stroke: currentColor;
                  stroke-linecap: round;
                  stroke-linejoin: round;
                "></path>
              <line
                x1="7.5"
                y1="3"
                x2="7.5"
                y2="6"
                style="
                  fill: none;
                  stroke: currentColor;
                  stroke-linecap: round;
                  stroke-linejoin: round;
                "
              ></line>
              <circle
                cx="7.5"
                cy="9"
                r="0.5"
                style="
                  fill: none;
                  stroke: currentColor;
                  stroke-linecap: round;
                  stroke-linejoin: round;
                "
              ></circle>
            </g>
          </g>
        </svg>
      </EmptyStatePlaceholder>
    </template>
  </TableTemplate>
</template>

<script>
import TableTemplate from "src/layout/Table/TableTemplate";
import tableFieldOptions from "src/mixins/tableFieldOptions";
import EmptyStatePlaceholder from "src/layout/EmptyStatePlaceholder.vue";
import TableHeader from "src/components/Product/ProductTable/TableHeader.vue";
import TableMainSearchField from "../layout/TableMainSearchField.vue";
import ReplenishmentDropdownTable from "../layout/ReplenishmentDropdownTable.vue";
import TableCell from "src/components/Table/TableCell.vue";
import FormButtonCheckbox from "src/components/Inputs/FormButtonCheckbox.vue";
import EditableTableItem from "../layout/EditTableItem/EditableTableItem.vue";
import table from "src/mixins/table";
import selectMultipleItems from "src/mixins/selectMultipleItems";
import FormSearchSelector from "../components/Inputs/FormSearchSelector.vue";
import ProductListItem from "../components/Product/ProductListItem.vue";
import TableDatePickerField from "../components/Inputs/TableDatePickerField.vue";
import FormDatePicker from "../components/Inputs/FormDatePicker.vue";

import mom from "moment"

const ins = mom()
const today = ins.format("YYYY-MM-DD")
const tomorrow = ins.add("day", 1).format("YYYY-MM-DD")

export default {
  metaInfo: {
    title: "Aanvulling",
  },
  data() {
    return {
      barcodeean: null,
      new_comment: "",
      awaitingEan: false,
      isActive: false,
      label: "Download Latest replenishment",
      deliverySku: null,
      number: "",
      sku: "",

      newDatePlannedShipment: today,
      newOrderDateShipment: tomorrow,
      newDelivery: {
        sku: "",
      },
    };
  },
  components: {
    TableDatePickerField,
    FormSearchSelector,
    ReplenishmentDropdownTable,
    TableHeader,
    TableMainSearchField,
    ProductListItem,
    EmptyStatePlaceholder,
    TableTemplate,
    TableCell,
    FormButtonCheckbox,
    FormDatePicker,
    EditableTableItem,
  },
  mixins: [
    selectMultipleItems,
    tableFieldOptions,
    table({
      pageReference: 'replenishment',

      /*
        "id": 48,
        "sku": "1198",

        "orderNumber": "RPHD071221",
        "newDatePlannedShipment": "2021-08-12T00:00:00.000Z",
        "newOrderDateShipment": "2021-07-12T00:00:00.000Z",
        "merchantCode": "hd",
        "comment": "Zakaria 9",
        "quantity": 0,
        "getData": 0,
        "dateAdded": "2021-12-07T07:47:49.000Z",
        "updatedAt": "2021-12-07T07:47:49.000Z"
      */

      columnTranslationMaps: {
        /* GOOGLE TRANSLATED */

        SKU: "sku",

        Bestellingsnummer: "orderNumber",
        "Datum geplande verzending": "datePlannedShipment",
        "Besteldatum verzending": "orderDateShipment",
        Verkoperscode: "merchantCode",
        Opmerking: "comment",
        Hoeveelheid: "quantity",
        "Gegevens verkrijgen": "getData",
        "Datum toegevoegd": "dateAdded",
        "Bijgewerkt op": "updatedAt",
        "Added By": "addedBy",
        Naam: "product_name"
      },
      disableCache: true,
      disableAlphabeticalSort: true,

      params: {
        dateAdded: "",
      },

      page_size: 50,
      toISOFields: ["dateAdded"],

      defaultColumns: [
        "Naam",
        "SKU",
        "Opmerking",
        "Hoeveelheid",
        "Datum geplande verzending",
        "Besteldatum verzending",
      ],
      columnConfigurationId: "id",

      route: "/api/deliveries",
    }),
  ],
  created() {
    this.getInitialData()
  },
  methods: {
    selectItem() {
      this.$nextTick(() => this.addDelivery())
    },
    async updateQuantity(id, sku, quantity) {
      await this.updateSingleTableRowById(id, {
        id,
        sku,
        quantity
      }, "Levering bijgewerkt!", ['quantity'])
    },
    async updateDeliveryWithMessage(id, data, removeMessage) {
      const stored = this.result.find((item) => item.id === id);
      const obj = {
        sku: stored.sku,
        quantity: stored.quantity,
        orderNumber: stored.orderNumber,
        comment: stored.comment,

        ...data,
      }

      if (data.datePlannedShipment) obj.datePlannedShipment = data.datePlannedShipment + " " + mom().format("HH:mm")
      if (data.orderDateShipment) obj.orderDateShipment = data.orderDateShipment + " " + mom().format("HH:mm")

      if (
        !removeMessage
          ? await this.$handlePromise(this.axios.put(`/api/deliveries/${id}`, obj))
          : await this.axios.put(`/api/deliveries/${id}`, obj)
      ) {
        this.$store.dispatch('clearCacheSearch', "deliveries")
        !removeMessage && this.$success("Levering bijgewerkt!");

        if (!removeMessage) {
          Object.keys(data).forEach((field) => this.refetchRow(id, field));
        } else {
          Object.keys(data).forEach((field) => this.refetchRow(id));
        }
      }
    },
    async addDelivery() {
      if (!this.newDelivery.sku) {
        return this.$error("Selecteer een product");
      }
      if (!this.number || !this.new_comment) {
        return this.$error("Nummer- en commentaarvelden zijn verplicht!")
      }

      const payload = {
        quantity: 1,
        sku: this.newDelivery.sku,
        orderNumber: this.number,
        comment: this.new_comment,

        datePlannedShipment: this.newDatePlannedShipment,
        orderDateShipment: this.newOrderDateShipment,
      }

      await this.$handlePromise(async () => {
        await this.axios.post(`/api/deliveries`, payload)
        this.$store.dispatch('clearCacheSearch', "deliveries")

        this.$refs.product.deactivate();

        this.freshRefetch();
      });
    },
    confirmDelete(item) {
      this.$confirmAction(() => this.deleteDeliveries(item.id), {
        title: "Replenishment verwijderen",
        description: `Weet u zeker dat u het leveringsrecord van <b>${item.sku}</b> wilt verwijderen?`,
        buttonSave: "Verwijderen",
      });
    },
    async deleteDeliveries(id) {
      await this.$handlePromise(this.axios.delete(`/api/deliveries/${id}`));
      this.$store.dispatch('clearCacheSearch', "deliveries")
      this.$success("Replenishment verwijderd!");
      this.freshRefetch();
    },
    confirmBulkDelete() {
      this.$confirmAction(() => this.deleteAll(), {
        title: "Replenishment verwijderen",
        description: "Weet u zeker dat u de aflevergegevens wilt verwijderen?",
        buttonSave: "Verwijderen"
      })
    },
    async deleteAll() {
      await this.$handlePromise(this.axios.delete('/api/deliveries'));
      this.$store.dispatch('clearCacheSearch', "deliveries")
      this.freshRefetch();
    },
    checkDropdownPath(evt) {
      const path = evt.composedPath();
      let found;
      for (const node of path) {
        if (node === this.$refs["download-dropdown"]) {
          found = true;
          break;
        }
      }

      if (!found) this.isActive = false;
    },
    updateAllCommentsAndRows() {
      if (this.commentTimeout) {
        clearTimeout(this.commentTimeout)
      }


      this.commentTimeout = setTimeout(async () => {
        const ids = this.result.map(item => item.id)
        const data = {
          comment: this.new_comment,
          orderNumber: this.number,
          datePlannedShipment: this.newDatePlannedShipment,
          orderDateShipment: this.newOrderDateShipment,
        }

        await Promise.all(
          ids.map(id => this.updateDeliveryWithMessage(id, data, true))
        )

        this.$success("Leveringen bijgewerkt!")
      }, 200)
    },
    getInitialData() {
      this.$handlePromise(async () => {
        const { data } = await this.axios.get('/api/deliveries')
        const { results } = data
        if (results && results.length) {
          this.number = results[0].orderNumber
          this.new_comment = results[0].comment
        }
      })
    }
  },
  computed: {
    isMobile() {
      return Math.min(window.screen.width, window.screen.height) < 640;
    },
  },
  watch: {
    new_comment() {
      this.updateAllCommentsAndRows()
    },
    number() {
      this.updateAllCommentsAndRows()
    },
    newDatePlannedShipment() {
      this.updateAllCommentsAndRows()
    },
    newOrderDateShipment() {
      this.updateAllCommentsAndRows()
    },

    barcodeean: async function (val) {
      if (!this.awaitingEan && this.barcodeean && this.barcodeean.length == 13) {
        setTimeout(async () => {
          const data = await this.$handlePromise(
            this.axios
              .post(`/api/deliveries`, { ean: this.barcodeean })
              .then((res) => (this.barcodeean = null))
              .catch(function (error) {
                console.log(error);
              })
          );
          this.$store.dispatch('clearCacheSearch', "deliveries")
          this.refetch();
          this.awaitingEan = false;
        }, 1000); // 1 sec delay
      }
      this.awaitingEan = true;
    },

    isActive(showingDropdown) {
      if (showingDropdown) {
        window.addEventListener("click", this.checkDropdownPath, {
          passive: true,
        });
      } else {
        window.removeEventListener("click", this.checkDropdownPath);
      }
    },
  },
};
</script>

<style scoped lang="scss">

.delete-icon {
  background-color: #C7314A;
  color: white;
  width: 20px;
  height: 20px;
  padding: 0px;
  border-radius: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-select {
  border: none;
  outline: 0;
}

.select-wrapper {
  width: 15%;
  position: relative;
}
select.form-select.form-control {
  height: 38px !important;
}
.select-inner-wrapper {
  flex-basis: 100%;
  position: relative;
}

.download-dropdown input {
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.arrow {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  color: #e3e3e3;
}

.d-flex.main-box {
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 1px;
}

.export-div {
  z-index: 99999;
  position: absolute;
  width: 300px;
  height: auto;
  min-height: 150px;
  max-height: 300px;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 5px;
  box-shadow: 1px 1px 0.05px rgb(0 0 0 / 13%);
  overflow: auto;
  right: 0;
  top: calc(100% + 4px);
}
img.up-arrow-part {
  height: 11px;
  width: 11px;
  transform: rotate(0deg);
  transition-duration: 0.15s;
}

img.up-arrow-part.isActive {
  transform: rotate(180deg) !important;
}
.arrow,
svg {
  transition-duration: 0.15s;
}

.showing {
  color: #212529;
}

svg {
  transform: rotate(0deg);
}

.showing svg {
  transform: rotate(-90deg);
}
@media screen and (max-width: 425px) {
  .search-filed-main {
    width: 100% !important;
  }
  .search-filed,
  .export-date {
    margin-left: 0px !important;
    margin-top: 0px !important;
  }
  .mobile-align {
    margin-left: 0px !important;
  }
  .search-filed-mt {
    margin-top: 11px !important;
  }
}

.replenishment-filters {
  row-gap: 0.5rem;
}
</style>
